/* @jsx h */
import { defineComponent, h } from "vue";
import { CUSTOMER_STATUS_LEVELS, RestaurantCustomer } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";
import { ExternalLink } from "@app/vue/components/ExternalLink";
import { formatDate } from "../../utils";
import { customerStatusLabel } from "./customerDetailHelpers";

export const CustomerCRMHeader = defineComponent<{
  customer: RestaurantCustomer;
  avatarColor: string;
  initials: string;
  birthdayLabel: string;
  isVIP: boolean;
  isEditing: boolean;
  isTogglingVIP: boolean;
  hideCloseButton?: boolean;
  onToggleVIP: () => void;
  onStartEditing: () => void;
  onClose: () => void;
  hideName?: boolean;
}>({
  name: "CustomerCRMHeader",
  props: [
    "customer",
    "avatarColor",
    "initials",
    "birthdayLabel",
    "isVIP",
    "isEditing",
    "isTogglingVIP",
    "hideCloseButton",
    "onToggleVIP",
    "onStartEditing",
    "onClose",
    "hideName",
  ],
  setup(props) {
    const { t } = useLocalization();

    return () => (
      <div class="px-4 md:px-6 py-4 border-b border-base-content/8 bg-base-200/20 flex-shrink-0">
        <div class="flex items-start gap-3 md:gap-4">
          <div class="relative flex-shrink-0">
            <div
              class={`w-14 h-14 rounded-xl bg-gradient-to-br ${props.avatarColor} flex items-center justify-center text-accent text-base font-bold shadow-lg`}
            >
              {props.initials}
            </div>
            {props.isVIP && (
              <div class="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center shadow ring-2 ring-base-200">
                <span class="material-symbols-outlined text-[11px] text-accent font-bold">
                  star
                </span>
              </div>
            )}
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              {!props.hideName ? (
                <h2 class="text-lg font-bold text-accent truncate">
                  {props.customer.name ||
                    t("reservations.customers.unnamed_customer")}
                </h2>
              ) : null}

              {props.customer.status_level && (
                <span
                  class={`badge badge-xs ${
                    props.isVIP
                      ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                      : props.customer.status_level ===
                          CUSTOMER_STATUS_LEVELS.NEW
                        ? "badge-primary badge-outline"
                        : props.customer.status_level ===
                            CUSTOMER_STATUS_LEVELS.INACTIVE
                          ? "badge-error badge-outline"
                          : "badge-ghost"
                  }`}
                >
                  {customerStatusLabel(props.customer.status_level, t)}
                </span>
              )}
            </div>
            <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5 text-xs text-base-content/45">
              {props.customer.email && (
                <ExternalLink
                  href={`mailto:${props.customer.email}`}
                  class="flex min-w-0 items-center gap-1 truncate hover:text-primary transition-colors"
                >
                  <span class="material-symbols-outlined text-[12px]">
                    mail
                  </span>
                  {props.customer.email}
                </ExternalLink>
              )}
              {props.customer.phone && (
                <ExternalLink
                  href={`tel:${props.customer.phone}`}
                  class="flex items-center gap-1 hover:text-primary transition-colors"
                >
                  <span class="material-symbols-outlined text-[12px]">
                    phone
                  </span>
                  {props.customer.phone}
                </ExternalLink>
              )}
            </div>
            <div class="flex items-center gap-1.5 flex-wrap text-[10px] text-base-content/25 mt-0.5">
              <span>
                {t("reservations.customers.customer_since", [
                  formatDate(props.customer.customer_created_at),
                ])}
              </span>
              {props.birthdayLabel && (
                <span class="badge badge-xs badge-ghost gap-0.5">
                  <span class="material-symbols-outlined text-[10px]">
                    cake
                  </span>
                  {props.birthdayLabel}
                </span>
              )}
            </div>
          </div>

          <div class="flex items-center gap-1 flex-shrink-0">
            <button
              onClick={props.onToggleVIP}
              disabled={props.isTogglingVIP}
              class={`btn btn-sm btn-square rounded-lg ${
                props.isVIP
                  ? "bg-amber-500/15 text-amber-400 border-amber-500/30 hover:bg-amber-500/25"
                  : "btn-ghost hover:bg-amber-500/10 hover:text-amber-400"
              }`}
              aria-label={
                props.isVIP
                  ? t("reservations.customers.remove_vip_status")
                  : t("reservations.customers.make_vip")
              }
            >
              {props.isTogglingVIP ? (
                <span class="loading loading-spinner loading-xs" />
              ) : (
                <span class="material-symbols-outlined text-lg">
                  {props.isVIP ? "star" : "star_outline"}
                </span>
              )}
            </button>
            {props.customer.email && (
              <ExternalLink
                href={`mailto:${props.customer.email}`}
                class="btn btn-sm btn-square rounded-lg btn-ghost hover:bg-info/10 hover:text-info"
                aria-label={t("reservations.customers.send_email")}
              >
                <span class="material-symbols-outlined text-lg">mail</span>
              </ExternalLink>
            )}
            {!props.isEditing && (
              <button
                onClick={props.onStartEditing}
                class="btn btn-sm btn-square rounded-lg btn-ghost"
                aria-label={t("reservations.customers.edit_customer")}
              >
                <span class="material-symbols-outlined text-lg">edit</span>
              </button>
            )}
            {!props.hideCloseButton && (
              <button
                onClick={props.onClose}
                class="btn btn-sm btn-square rounded-lg btn-ghost"
                aria-label={t("reservations.customers.close_customer_detail")}
              >
                <span class="material-symbols-outlined text-lg">close</span>
              </button>
            )}
          </div>
        </div>
      </div>
    );
  },
});
